import { Inject, Injectable } from '@angular/core';

import {
    AuthService,
    RidangoEpic,
    RidangoReduxAction,
    SetCustomerProfileInformation,
    SetProfileInformationDTO
} from '@ridango/core';

import { ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { CustomerService, CustomerRegistrationCompleteRequest } from '../customer.service';
import { CustomerActions } from './customer.actions';
import Env from '@ridango/core/env';
import { ApplicationFacade } from '../../../lib/application';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';

@Injectable({
    providedIn: 'root'
})
export class CustomerEpics implements RidangoEpic {
    constructor(
        @Inject('ENV') private env: Env,
        private actions: CustomerActions,
        private service: CustomerService,
        private authService: AuthService,
        private applicationFacade: ApplicationFacade,
        private router: Router,
        private translate: TranslateService
    ) {}

    register() {
        return [
            this.getCustomerProfile,
            this.saveCustomerProfile,
            this.completeCustomerRegistration,
            this.sendCustomerProfileDeletionEmail,
            this.deleteCustomerProfile,
            this.sendCustomerNewEmail,
            this.saveCustomerNewEmail,
            this.sendCustomerLang
        ];
    }

    getCustomerProfile = (action$: ActionsObservable<any>) =>
        action$.ofType(CustomerActions.GET_CUSTOMER_PROFILE).pipe(
            filter(() => this.authService.getLoginStatus()),
            switchMap(() =>
                this.service.fetchCustomerProfile().pipe(
                    map((response) => this.actions.setCustomerProfile(response)),
                    catchError((error) => of(this.actions.getCustomerProfileFailed(error)))
                )
            )
        );

    saveCustomerProfile = (
        action$: ActionsObservable<RidangoReduxAction<SetProfileInformationDTO | SetCustomerProfileInformation>>
    ) =>
        action$.ofType(CustomerActions.SAVE_CUSTOMER_PROFILE).pipe(
            switchMap((action: RidangoReduxAction<SetProfileInformationDTO | SetCustomerProfileInformation>) => {
                return this.service.saveCustomerProfile(action.payload).pipe(
                    map((response) => {
                        return this.actions.saveCustomerProfileSuccess(response);
                    }),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.saveCustomerProfileFailed(browserError));
                        }

                        return of(this.actions.saveCustomerProfileFailed(error));
                    })
                );
            })
        );

    completeCustomerRegistration = (
        action$: ActionsObservable<RidangoReduxAction<CustomerRegistrationCompleteRequest>>
    ) =>
        action$.ofType(CustomerActions.COMPLETE_CUSTOMER_REGISTRATION).pipe(
            switchMap((action: RidangoReduxAction<CustomerRegistrationCompleteRequest>) => {
                return this.service.completeCustomerRegistration(action.payload).pipe(
                    map((response) => {
                        return this.actions.completeCustomerRegistrationSuccess(response);
                    }),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.completeCustomerRegistrationFailed(browserError));
                        }

                        return of(this.actions.completeCustomerRegistrationFailed(error));
                    })
                );
            })
        );

    sendCustomerProfileDeletionEmail = (action$: ActionsObservable<any>) =>
        action$.ofType(CustomerActions.SEND_CUSTOMER_PROFILE_DELETION_EMAIL).pipe(
            switchMap((_action: RidangoReduxAction) => {
                return this.service.sendCustomerProfileDeletionEmail().pipe(
                    map(() => {
                        this.authService.signOut();
                        if (Capacitor.isNativePlatform()) {
                            this.applicationFacade.setMobileMenuOpen(true);
                        }
                        this.router.navigate([this.translate.currentLang]);
                        return this.actions.sendCustomerProfileDeletionEmailSuccess(true);
                    }),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.sendCustomerProfileDeletionEmailFailed(browserError));
                        }

                        return of(this.actions.sendCustomerProfileDeletionEmailFailed(error));
                    })
                );
            })
        );

    deleteCustomerProfile = (action$: ActionsObservable<any>) =>
        action$.ofType(CustomerActions.DELETE_CUSTOMER_PROFILE).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.deleteCustomerProfile(action.payload).pipe(
                    map(() => {
                        this.authService.signOut();
                        this.applicationFacade.setMobileMenuOpen(true);
                        this.router.navigate([this.translate.currentLang]);

                        return this.actions.deleteCustomerProfileSuccess(true);
                    }),
                    catchError((_error) => {
                        return of(this.actions.deleteCustomerProfileFailed({ error: 'LOCAL_CUSTOMER_FORGET_FAILED' }));
                    })
                );
            })
        );

    sendCustomerNewEmail = (action$: ActionsObservable<any>) =>
        action$.ofType(CustomerActions.SEND_CUSTOMER_NEW_EMAIL).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.changeCustomerEmail({ email_change_request: action.payload }).pipe(
                    map(() => this.actions.sendCustomerNewEmailSuccess(true)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.sendCustomerNewEmailFailed(browserError));
                        }
                        return of(this.actions.sendCustomerNewEmailFailed(error));
                    })
                );
            })
        );

    saveCustomerNewEmail = (action$: ActionsObservable<any>) =>
        action$.ofType(CustomerActions.SAVE_CUSTOMER_NEW_EMAIL).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.verifyCustomerEmail(action.payload).pipe(
                    map(() => this.actions.saveCustomerNewEmailSuccess(true)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.saveCustomerNewEmailFailed(browserError));
                        }
                        return of(this.actions.saveCustomerNewEmailFailed(error));
                    })
                );
            })
        );

    sendCustomerLang = (action$: ActionsObservable<any>) =>
        action$.ofType(CustomerActions.SEND_CUSTOMER_LANG).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.sendCustomerLang({ lang: action.payload }).pipe(
                    map(() => this.actions.sendCustomerLangSuccess(true)),
                    catchError((error) => {
                        if (error.name && error.status === 0) {
                            const browserError = { error: error.name };
                            return of(this.actions.sendCustomerLangFailed(browserError));
                        }
                        return of(this.actions.sendCustomerLangFailed(error));
                    })
                );
            })
        );
}
